import { useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useActivities, useTrails, useWeather, useTides } from '../hooks/data.js';
import { scoreActivityAt } from '../lib/scoring.js';
import ActivityCard from '../components/ActivityCard.jsx';
import ActivityDetail from '../components/ActivityDetail.jsx';

const SORTS = [
  { id: 'score', label: 'Score' },
  { id: 'distance', label: 'Length' },
  { id: 'difficulty', label: 'Difficulty' },
  { id: 'drive', label: 'Drive' },
];

const DIFF_ORDER = { Easy: 1, Moderate: 2, Hard: 3 };

// Hiking-focused now that Drives, Whale Watching, Endicott Arm,
// Excursions, and Meals have dedicated tabs. Tours / wildlife /
// in-town entries stay in activities.json (other surfaces still
// reference them) — just not rendered here.
const FILTERS = [
  { id: 'all', label: 'All trails' },
  { id: 'easy', label: 'Easy' },
  { id: 'moderate', label: 'Moderate' },
  { id: 'hard', label: 'Hard' },
];

export default function Activities() {
  const { tripId, trip } = useTrip();
  const activities = useActivities(tripId);
  const trails = useTrails();
  const lat = trip?.destination?.lat ?? 58.3019;
  const lng = trip?.destination?.lng ?? -134.4197;
  const weather = useWeather({ lat, lng });
  const tides = useTides();

  const [sort, setSort] = useState('score');
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);

  const enriched = useMemo(() => {
    if (!activities.data?.activities) return [];
    const trailMap = trails.data?.trails || {};
    const now = new Date();
    const w = currentHour(weather.data, now);
    const tide = currentTide(tides.data, now);

    return activities.data.activities
      // Hiking-only render: drop tours / wildlife / in-town (other tabs).
      .filter((a) => a.type === 'trail' || !!a.trail_id)
      .map((a) => {
        const trail = a.trail_id ? trailMap[a.trail_id] : null;
        const score =
          w &&
          scoreActivityAt({
            activity: a,
            when: now,
            weather: w,
            tideSample: tide,
            originLat: lat,
            originLng: lng,
          });
        return { activity: a, trail, score };
      });
  }, [activities.data, trails.data, weather.data, tides.data, lat, lng]);

  const visible = useMemo(() => {
    let list = enriched;
    if (filter !== 'all') {
      // Difficulty filter (Easy/Moderate/Hard) — pulls from the AllTrails
      // difficulty field via the trail join.
      const want = filter[0].toUpperCase() + filter.slice(1);
      list = list.filter((e) => e.trail?.difficulty === want);
    }

    list = [...list].sort((a, b) => {
      switch (sort) {
        case 'score':
          return (b.score?.score ?? -1) - (a.score?.score ?? -1);
        case 'distance': {
          const av = a.trail?.length_miles ?? Infinity;
          const bv = b.trail?.length_miles ?? Infinity;
          return av - bv;
        }
        case 'difficulty': {
          const av = DIFF_ORDER[a.trail?.difficulty] ?? 99;
          const bv = DIFF_ORDER[b.trail?.difficulty] ?? 99;
          return av - bv;
        }
        case 'drive': {
          const av = a.activity.drive_time_from_lodging_minutes ?? Infinity;
          const bv = b.activity.drive_time_from_lodging_minutes ?? Infinity;
          return av - bv;
        }
        default:
          return 0;
      }
    });
    return list;
  }, [enriched, filter, sort]);

  const open = visible.find((v) => v.activity.id === openId) || null;

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          condition-ranked · AllTrails-powered
        </p>
        <h1 className="mt-1 font-serif text-2xl">Hiking</h1>
      </header>

      <Toolbar sort={sort} setSort={setSort} filter={filter} setFilter={setFilter} />

      <DataStatus
        activities={activities}
        trails={trails}
        weather={weather}
        tides={tides}
      />

      {visible.length === 0 && !activities.loading && (
        <div className="card px-4 py-6 text-center text-sm text-fg-secondary">
          No activities match this filter.
        </div>
      )}

      <div className="space-y-2">
        {visible.map(({ activity, trail, score }) => (
          <ActivityCard
            key={activity.id}
            activity={activity}
            trail={trail}
            score={score}
            onOpen={() => setOpenId(activity.id)}
          />
        ))}
      </div>

      {open && (
        <ActivityDetail
          activity={open.activity}
          trail={open.trail}
          score={open.score}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

function Toolbar({ sort, setSort, filter, setFilter }) {
  return (
    <div className="space-y-2">
      <SegmentedRow label="Sort" items={SORTS} value={sort} onChange={setSort} />
      <SegmentedRow label="Type" items={FILTERS} value={filter} onChange={setFilter} />
    </div>
  );
}

function SegmentedRow({ label, items, value, onChange }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">{label}</p>
      <div className="mt-1 flex gap-1 overflow-x-auto pb-1">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => onChange(it.id)}
            className={[
              'shrink-0 rounded-full border px-3 py-1 font-mono text-xs uppercase tracking-wider transition',
              value === it.id
                ? 'border-action bg-action text-action-fg'
                : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
            ].join(' ')}
          >
            {it.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function DataStatus({ activities, trails, weather, tides }) {
  const items = [
    { label: 'activities', state: activities },
    { label: 'AllTrails cache', state: trails },
    { label: 'weather', state: weather },
    { label: 'tides', state: tides },
  ];
  const anyLoading = items.some((i) => i.state.loading);
  const errors = items.filter((i) => i.state.error);

  if (errors.length === 0 && !anyLoading) return null;

  return (
    <div className="card px-3 py-2 text-xs text-fg-secondary">
      {anyLoading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading {items.filter((i) => i.state.loading).map((i) => i.label).join(' · ')}
        </p>
      )}
      {errors.map(({ label, state }) => (
        <p key={label} className="text-danger">
          {label} unavailable — {state.error.message}
        </p>
      ))}
    </div>
  );
}

function currentHour(weatherData, now) {
  if (!weatherData?.hourly?.time?.length) return null;
  const ms = now.getTime();
  let bestIdx = 0;
  let bestDiff = Infinity;
  for (let i = 0; i < weatherData.hourly.time.length; i += 1) {
    const t = new Date(weatherData.hourly.time[i]).getTime();
    const diff = Math.abs(ms - t);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }
  const out = {};
  for (const k of Object.keys(weatherData.hourly)) {
    if (Array.isArray(weatherData.hourly[k])) out[k] = weatherData.hourly[k][bestIdx];
  }
  return out;
}

function currentTide(tideData, now) {
  if (!tideData?.series?.length) return null;
  const ms = now.getTime();
  let best = tideData.series[0];
  let bestDiff = Infinity;
  for (const row of tideData.series) {
    const diff = Math.abs(ms - new Date(row.t).getTime());
    if (diff < bestDiff) {
      bestDiff = diff;
      best = row;
    }
  }
  return best;
}
